import axios from "axios"
import { ServiceError } from "@core/exceptions"

/**
 * Fonction permettant de gérer la logique d'appel d'une API et de gérer les erreurs qui peuvent survenir.
 * Si une erreur survient, elle est transformée en `ServiceError` pour être traitée plus facilement dans les composants.
 *
 * Ceci est un exemple d'utilisation lors de l'appel d'une API :
 * ```typescript
 * handleServiceCall(async () => {
 *    const { data } = await axios.get("/api/gateway/auth/verify-key", {
 *       params: {
 *         type: "data",
 *      },
 *     headers: {
 *      "x-widget-key": key,
 *    },
 * })
 * ```
 *
 * @param serviceCall - Fonction d'appel de l'API
 * @returns
 */
export async function handleServiceCall<T>(
    serviceCall: () => Promise<T>
): Promise<T> {
    return serviceCall().catch((error) => {
        if (axios.isCancel(error)) {
            throw error
        }
        console.error(error)
        if (error.response) {
            throw new ServiceError(
                {
                    message:
                        (error.response.data?.detail?.message
                            ? error.response.data.detail.message
                            : error.response.data.message) ??
                        "Une erreur est survenue",
                    code:
                        (error.response.data?.detail?.code
                            ? error.response.data.detail.code
                            : error.response.data.code) ?? "unknown",
                    details: error.response.data?.detail?.details
                        ? error.response.data.detail.details
                        : (error.response.data?.details ?? undefined),
                    description: error.message,
                },
                error.response.status
            )
        } else {
            throw new ServiceError(
                {
                    message: "Une erreur inconnue est survenue",
                    code: "UNKNOWN",
                    description: error.message,
                },
                500
            )
        }
    })
}
